import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { CookieConsentProvider } from "@/ui/components/cookie-consent/index";
import { ThemeProvider } from "@/ui/components/theme-provider";
import { Toaster } from "@/ui/components/ui/sonner";
import { TooltipProvider } from "@/ui/components/ui/tooltip";

export type RouterContext = {
  queryClient: QueryClient;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

/**
 * The app-wide shell, equivalent to the Next.js root `layout.tsx`.
 *
 * The document-level concerns that lived there — `<html>`/`<body>` classes,
 * fonts, the `noindex` robots directives — moved to `index.html`, which is the
 * document now. What remains here are the providers that must wrap every route.
 *
 * Light is the default theme; dark applies only when chosen. The
 * pre-hydration script that drops a legacy `"system"` preference lives in
 * `index.html`, so it still runs before first paint with no flash.
 */
function RootLayout() {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem={false}
      disableTransitionOnChange
    >
      <TooltipProvider>
        <Outlet />
      </TooltipProvider>
      <Toaster position="top-center" richColors closeButton />
      <CookieConsentProvider />
      {import.meta.env.DEV ? <TanStackRouterDevtools position="bottom-right" /> : null}
    </ThemeProvider>
  );
}
